// Stript version of Itslearning\UI\Itslearning.Web.UI.Controls.Common\TypeScript\IframeContentHeightReporter.ts
// Script to resize frame to avoid duble scrollbars.
class CallbackDebouncer {
    /**
     * Creates a new instance of `CallbackDebouncer`.
     * @param delayedCallback callback function, which would be called with timeout.
     */
    constructor(delayedCallback) {
        this.delayedCallback = delayedCallback;
    }

    /**
     * Invokes callback with timeout, if there was already a call to the callback, it would be canceled.
     * And the current call will be scheduled instead.
     * @param callbackArg argument to pass to a callback.
     * @param timeoutInMilliseconds timeout in milliseconds, after which the callback will be invoked.
     */
    invokeCallback(callbackArg, timeoutInMilliseconds) {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }

        this.timeoutId = setTimeout(() => { this.delayedCallback(callbackArg); }, timeoutInMilliseconds);
    }
}

class GenericMessage {
    constructor(messageName) {
        this.messageName = messageName;
    }
}

class GenericMessageWithData extends GenericMessage {
    constructor(messageName, extendedData) {
        super(messageName);
        this.extendedData = extendedData;
    }
}

function makePostMessageCall(parentFrame, data) {
    parentFrame.postMessage(JSON.stringify(data), '*');
}

/* Use this class if a document is viewed inside iframe and you want to report the height of it to a parent frame */
export class IframeContentHeightReporter {
    // sent from extension iframe to the itslearing to notify that view page was opened (for resizing extension frame)

    constructor() {
        this.extensionContentOpened = 'extensionContentOpened';
        this.contentDimensions = {
            width: 0,
            height: 0,
        };

        this.defaultDimensions = {
            width: -1,
            height: -1
        };

        if (document.readyState === 'complete') {
            this.reportContentDimensionsIfChanged();
        } else {
            window.addEventListener('load', () => this.reportContentDimensionsIfChanged());
        }

        // Report height if window size is changed
        const windowResizeDebouncer = new CallbackDebouncer(() => this.reportContentDimensions(this.calculateDocumentDimensions()));

        window.addEventListener('resize', () => { windowResizeDebouncer.invokeCallback(undefined, 250); });
        document.addEventListener('cssmodal:show', () => { this.reportContentDimensions(IframeContentHeightReporter.defaultDimensions); });
    }

    calculateDocumentDimensions() {
        const style = getComputedStyle(document.querySelector('#SurveyMain'));

        const marginTop = parseInt(style.marginTop);
        const marginBottom = parseInt(style.marginBottom);

        const dimension = {
            width: document.querySelector('#SurveyMain').offsetWidth,
            height: document.querySelector('#SurveyMain').offsetHeight + (marginTop + marginBottom)
        };

        return dimension;
    }

    // Send a post message to a parent frame with actual height of the document
    reportContentDimensions(dimensions) {
        const iframeDimensions = {
            iframeWidth: dimensions.width,
            iframeHeight: dimensions.height
        };

        makePostMessageCall(
            window.parent,
            new GenericMessageWithData(
                'extensionContentOpened',
                iframeDimensions
            )
        );
    }

    // Send a post message to a parent frame with actual height of the document
    reportContentDimensionsIfChanged() {
        const dimensions = this.calculateDocumentDimensions();

        if (this.contentDimensions.height !== dimensions.height || this.contentDimensions.width !== dimensions.width) {
            this.contentDimensions = dimensions;
            this.reportContentDimensions(this.contentDimensions);
        }
    }

    // Detects changes in DOM that can lead to changes in height of a document.
    // If document height is changed, reports current height to a parent frame
    setUpMutationObserver() {
        const attributeFilter = ['style', 'src', 'height', 'width', 'class', 'open', 'aria-selected', 'hidden'];

        // Define mutations that can change height of a document, so we watch them
        const observerConfiguration = {
            childList: true, // append/remove children
            attributes: true, // append/remove/modify attributes
            attributeFilter: attributeFilter, // filter attributes to observe
            characterData: true, // text changes
            subtree: true // changes should be tracked for all sub-children
        };
        const observer = new MutationObserver(mutations => {
            if (mutations.length > 0) {
                // Report changed height with a slight delay to prevent from sending old (not yet changed) height
                const mutationDebouncer = new CallbackDebouncer(() => this.reportContentDimensionsIfChanged());

                mutationDebouncer.invokeCallback(undefined, 100);
            }
        });

        observer.observe(document.body, observerConfiguration);
    }

    setUpTimerObserver() {
        this.reportContentDimensionsIfChanged();

        if (this.onContentHeightChangeTimer) {
            clearTimeout(this.onContentHeightChangeTimer);
        }

        this.onContentHeightChangeTimer = setTimeout(() => this.setUpTimerObserver(), 200);
    }

    setUpChangeObservation() {
        // Browsers that support mutation observer
        // See full list here: http://caniuse.com/#feat=mutationobserver
        if ('MutationObserver' in window) {
            this.setUpMutationObserver();
        } else {
            this.setUpTimerObserver();
        }
    }

    static setUpIframeContentHeightReporter() {
        const contentHeightReporter = new IframeContentHeightReporter();

        contentHeightReporter.setUpChangeObservation();
    }
}
